import { useHomeSecurity } from '@contexts/HomeSecurityContext/HomeSecurityContext';
import { showToast } from '@design-system';
import { CreateHomeSecurityPayload, useUploadFileMutation, useCreateHomeSecurityMutation } from '@services';
import { transformSignatureToFile } from '@utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

export type HomeSecurityStep = {
    title: string;
    icon: React.ReactNode;
    onClick: () => void;
    isDone: boolean;
};

export const useHomeSecurityEmargementVM = () => {
    const navigate = useNavigate();
    const {
        setSignature,
        setSelectedUser: setSelectedUserHomeSecurity,
        setIsMinor: setIsMinorHomeSecurity,
        setContractType: setContractTypeHomeSecurity,
        worksiteId,
        workStep,
        selectedEPIs,
        worksitePresentation,
        worksitePresentationComment,
        otherWorkStep,
        passedModules
    } = useHomeSecurity();

    const signatureCanvasRef = useRef<SignatureCanvas>(null);
    const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const [isMinor, setIsMinor] = useState(false);
    const [comment, setComment] = useState<string>('');
    const [isInformationChecked, setIsInformationChecked] = useState(false);
    const [isEpiChecked, setIsEpiChecked] = useState(false);
    const [contractType, setContractType] = useState<CreateHomeSecurityPayload['contract_type'] | null>(null);

    const [uploadFile, { isError: isFileUploadError, error: fileUploadError, data: fileUploadData }] = useUploadFileMutation();
    const [
        createHomeSecurity,
        { isLoading: isCreatingHomeSecurity, isSuccess: isCreatingHomeSecuritySuccess, isError: isCreatingHomeSecurityError }
    ] = useCreateHomeSecurityMutation();

    useEffect(() => {
        if (!worksiteId || passedModules.length !== 5) {
            navigate('/sante-securite/home-security/menu');
        }
    }, [worksiteId, passedModules, navigate]);

    const handleClearSignature = () => {
        if (signatureCanvasRef.current) {
            signatureCanvasRef.current.clear();
            setIsSignatureEmpty(true);
        }
    };

    const handleSaveSignature = () => {
        if (signatureCanvasRef.current) {
            const isEmpty = signatureCanvasRef.current.isEmpty();
            if (isEmpty) {
                showToast({
                    message: 'Veuillez signer avant de continuer',
                    type: 'error'
                });
                return;
            }

            const dataUrl = signatureCanvasRef.current.toDataURL('image/png');
            const file = transformSignatureToFile(dataUrl);

            const formData = new FormData();
            formData.append('file', file);

            uploadFile(formData);
        }
    };

    const mapPayload = (passedSignature: string) => {
        if (isCreatingHomeSecurity || !selectedUser || !passedSignature || !workStep || !worksiteId || !contractType || !selectedEPIs || (workStep.includes("Autre") && !otherWorkStep)) {
            showToast({
                message: 'Une erreur est survenue, veuillez réessayer plus tard',
                type: 'error'
            });
            return;
        }
        return {
            request_user: selectedUser,
            signature: passedSignature,
            work_steps: workStep,
            contract_type: contractType,
            is_minor: isMinor,
            worksite_id: worksiteId,
            epi_received: selectedEPIs,
            worksite_presentation: worksitePresentation,
            ...(worksitePresentationComment && { worksite_presentation_comment: worksitePresentationComment }),
            ...(otherWorkStep && { other_work_step: otherWorkStep }),
            comment
        };
    };

    useEffect(() => {
        if (isFileUploadError && fileUploadError) {
            console.error('fileUploadError:', fileUploadError);
        }

        if (fileUploadData) {
            setSignature(fileUploadData.original);
            const payload = mapPayload(fileUploadData.original);
            if (!payload) return;
            createHomeSecurity(payload);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFileUploadError, fileUploadError, fileUploadData, setSignature, isMinor, navigate, createHomeSecurity]);

    useEffect(() => {
        if (selectedUser) {
            setSelectedUserHomeSecurity(selectedUser);
        }
    }, [selectedUser, setSelectedUserHomeSecurity]);

    useEffect(() => {
        setIsMinorHomeSecurity(isMinor);
    }, [isMinor, setIsMinorHomeSecurity]);

    useEffect(() => {
        if (contractType) {
            setContractTypeHomeSecurity(contractType);
        }
    }, [contractType, setContractTypeHomeSecurity]);

    useEffect(() => {
        if (isCreatingHomeSecurityError || isCreatingHomeSecurity) {
            return;
        }
        if (isCreatingHomeSecuritySuccess) {
            showToast({
                message: 'Accueil validé avec succès',
                type: 'success'
            });
            if (isMinor) {
                navigate('/sante-securite/home-security/minor-infos');
            } else {
                navigate('/sante-securite');
            }
        }
    }, [isCreatingHomeSecuritySuccess, isCreatingHomeSecurityError, isCreatingHomeSecurity, navigate, isMinor]);

    const hasFilledInfos = useMemo(() => {
        return selectedUser && !isSignatureEmpty && contractType;
    }, [selectedUser, isSignatureEmpty, contractType]);

    return {
        signatureCanvasRef,
        isSignatureEmpty,
        setIsSignatureEmpty,
        handleClearSignature,
        handleSaveSignature,
        selectedUser,
        setSelectedUser,
        isMinor,
        setIsMinor,
        hasFilledInfos,
        contractType,
        setContractType,
        isInformationChecked,
        setIsInformationChecked,
        isEpiChecked,
        setIsEpiChecked,
        comment,
        setComment
    };
};
